import _ from 'lodash';

import validateConfig from 'src/helpers/validation/config';

const defaultMessages = validateConfig.defaultMessages;

function validateString(config, value, options = {}) {
  let { regexp, exclude, fn, minLength, maxLength, oneOf, message } = config;
  if (typeof value != 'string' && isNaN(value)) return message;
  if (!options.noMinLength && typeof minLength === 'number' && value.length < minLength) {
    if (value.length === 0) {
      return config.requiredMessage;
    } else {
      return config.tooShortMessage;
    }
  }
  if (options.noMinLength && value === '') return null;
  if (typeof maxLength === 'number' && value.length > maxLength) return message;
  if (value.length > 0 && regexp && !value.match(regexp)) return message;
  if (exclude && exclude.indexOf(value.toLowerCase()) !== -1) return `"${value}" is reserved word`;
  if (oneOf && oneOf.indexOf(value) === -1) return message;
  if (fn && !fn(value, options)) return message;
  return null;
}

function validateNumber(config, value, options = {}) {
  let { fn, min, max, message } = config;
  if (typeof value != 'number' || isNaN(value)) return message;
  if (typeof min === 'number' && value < min) return message;
  if (typeof max === 'number' && value > max) return message;
  if (typeof fn === 'function' && !fn(value, options)) return message;
  return null;
}

function validateBoolean(config, value, options = {}) {
  let { fn, message } = config;
  if (typeof value != 'boolean') return message;
  if (typeof fn === 'function' && !fn(value, options)) return message;
  return null;
}

function validateArray(config, value, options = {}) {
  let { fn, minLength, maxLength, items, message, minLengthMessage, maxLengthMessage } = config;
  if (!Array.isArray(value)) return message;
  if (typeof minLength === 'number' && value.length < minLength)
    return (minLengthMessage || message).replace('{{val}}', minLength);
  if (typeof maxLength === 'number' && value.length > maxLength) {
    return (maxLengthMessage || message).replace('{{val}}', maxLength);
  }
  if (typeof items === 'string') {
    if (validateConfig[items]) {
      for (var i = value.length - 1; i >= 0; i--) {
        let err = validate(items, value[i]);
        if (err) return err;
      }
    } else {
      console.warn('missing validate config for items:', items);
    }
  }
  let msg;
  if (fn && (msg = fn(value, options)) !== true) return typeof msg != 'boolean' ? msg : message;
  return null;
}

function validateObject(config, value, options = {}) {
  let { fn, message } = config;
  let msg;
  if (!value || typeof value !== 'object') return message;
  if (Array.isArray(value)) return message;
  if (fn && (msg = fn(value, options)) !== true) return typeof msg != 'boolean' ? msg : message;
  return null;
}

function validateObjectId(config, value) {
  let { message } = config;
  if (!value) return message;
  if (typeof value !== 'string') return message;
  return null;
}

function validateFiles(config, value) {
  let { message } = config;
  if (!(value && value[0])) return message;
  return null;
}

function validateAny(config, value, options = {}) {
  let { fn, message } = config;
  if (typeof value === 'undefined' || value === null) return message;
  if (fn && !fn(value, options)) return message;
  return null;
}

function getConfig(key) {
  let conf = null;
  if (Array.isArray(key)) {
    let result = {};
    let firstConf = {};
    key.map((k, i) => {
      let curConf = getConfig(k);
      if (i == 0) firstConf = curConf;
      result = {
        ...result,
        ...curConf
      };
    });
    result.tooShortMessage = firstConf.tooShortMessage;
    result.requiredMessage = firstConf.requiredMessage;
    result.minLengthMessage = firstConf.minLengthMessage;
    result.maxLengthMessage = firstConf.maxLengthMessage;
    return result;
  }
  if (typeof key === 'string') {
    if (!validateConfig[key]) return null;
    if (typeof validateConfig[key] == 'string') key = validateConfig[key];
    conf = validateConfig[key];
  } else if (typeof key === 'object') {
    conf = key;
  }
  if (!conf) return null;
  if (!conf.message) conf.message = `${_.startCase(key)} is ${defaultMessages.regular}`;

  if (!conf.tooShortMessage)
    conf.tooShortMessage = `${_.startCase(key)} is ${defaultMessages.tooShort}`;
  if (!conf.requiredMessage)
    conf.requiredMessage = `${_.startCase(key)} is ${defaultMessages.required}`;
  if (!conf.minLengthMessage) conf.minLengthMessage = `${defaultMessages.minLength}`;
  if (!conf.maxLengthMessage) conf.maxLengthMessage = `${defaultMessages.maxLength}`;
  return conf;
}

function validate(key, value, options) {
  if (typeof key === 'function') key = key();
  let conf = getConfig(key);
  if (!conf) return null;
  if (value === null) return conf.message;
  let { type } = conf;
  if (!type) {
    console.warn('validation: type required', key);
    return null;
  }

  if (conf.required && (!value || (Array.isArray(value) && value.length === 0))) {
    return conf.requiredMessage;
  }

  switch (type) {
    case 'string':
      return validateString(conf, value, options);
    case 'number':
      return validateNumber(conf, value, options);
    case 'boolean':
      return validateBoolean(conf, value, options);
    case 'array':
      return validateArray(conf, value, options);
    case 'object':
      return validateObject(conf, value, options);
    case 'objectId':
      return validateObjectId(conf, value, options);
    case 'files':
      return validateFiles(conf, value, options);
    case 'any':
      return validateAny(conf, value, options);
    default:
      console.warn('validation: unknown type');
      return null;
  }
}

function isTooShort(key, value) {
  if (!key) return false;
  if (typeof value !== 'string') return false;
  let conf = getConfig(key);
  let minLength = (conf && conf.minLength) || 0;
  if (value.length < minLength) return true;
}

function getInvalidMessage(key) {
  let conf = getConfig(key);
  return (conf && conf.message) || defaultMessages.regular;
}

export { validate, isTooShort, getInvalidMessage };

export const validateObj = (body, params, options = {}) => {
  let errors = {
    length: 0
  };
  if (params) {
    Object.keys(params).forEach(function (remoteKey) {
      let err = validate(params[remoteKey], body[remoteKey], { body, ...options });
      if (err) {
        errors[remoteKey] = err;
        errors.length++;
      }
    });
  }
  return errors;
};
